"use client"

import { useState, useEffect } from "react"
import { collection, query, orderBy, onSnapshot, updateDoc, doc, deleteDoc, getDocs, serverTimestamp } from "firebase/firestore"
import { db, auth } from "@/lib/firebase"
import { Loader2, Trash2, MessageCircle, BookOpen, Send, UserCheck, HelpCircle, Eye, EyeOff } from "lucide-react"
import { toast } from "sonner"
import { onAuthStateChanged } from "firebase/auth"

interface FAQ {
  id: string
  courseId: string
  courseTitle?: string
  userId: string
  userName: string
  question: string
  answer: string | null
  createdAt: any
}

export default function FAQManager() {
  const [faqs, setFaqs] = useState<FAQ[]>([])
  const [courses, setCourses] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [replyText, setReplyText] = useState<{ [key: string]: string }>({})
  const [authReady, setAuthReady] = useState(false)

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setAuthReady(!!user)
    })
    return () => unsub()
  }, [])

  useEffect(() => {
    const fetchCourses = async () => {
       const snap = await getDocs(collection(db, "courses"))
       setCourses(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    }
    fetchCourses()
  }, [])

  useEffect(() => {
    if (courses.length === 0) return;

    setLoading(true)
    const allUnsubs: (() => void)[] = []
    
    courses.forEach(course => {
       const q = query(collection(db, "courses", course.id, "faqs"))
       const unsub = onSnapshot(q, (snap) => {
          const courseFaqs = snap.docs.map(d => ({ 
             id: d.id, 
             courseId: course.id, 
             courseTitle: course.title,
             ...d.data() 
          }))
          
          setFaqs(prev => {
             const otherFaqs = prev.filter(f => f.courseId !== course.id)
             return [...otherFaqs, ...courseFaqs].sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
          })
       })
       allUnsubs.push(unsub)
    })

    setLoading(false)
    return () => allUnsubs.forEach(u => u())
  }, [courses])

  const handleAnswer = async (faq: FAQ) => {
    const answer = replyText[faq.id]
    if (!answer?.trim()) return
    
    if (!auth.currentUser) {
      toast.error("Session expired. Please log in again.")
      return
    }

    try {
      await updateDoc(doc(db, "courses", faq.courseId, "faqs", faq.id), {
        answer,
        answeredAt: serverTimestamp(),
        approved: true 
      })
      toast.success("Response recorded!")
      setReplyText(prev => {
        const next = { ...prev }
        delete next[faq.id]
        return next
      })
    } catch (error: any) {
      console.error("FAQ Reply Error:", error)
      if (error.code === 'permission-denied') {
        toast.error("Permission Denied. Verify your Admin role.")
      } else {
        toast.error("Failed to save response")
      }
    }
  }

  const toggleVisibility = async (faq: FAQ) => {
    try {
      const newStatus = (faq as any).isVisible === false ? true : false
      await updateDoc(doc(db, "courses", faq.courseId, "faqs", faq.id), {
        isVisible: newStatus
      })
      toast.success(newStatus ? "FAQ visible" : "FAQ hidden")
      setFaqs(prev => prev.map(f => f.id === faq.id ? { ...f, isVisible: newStatus } : f))
    } catch (error) {
      toast.error("Failed to update visibility")
    }
  }

  const deleteFaq = async (faq: FAQ) => {
    if (!confirm("Permanently delete this question?")) return
    
    if (!auth.currentUser) {
      toast.error("Session expired. Please log in again.")
      return
    }

    try {
      await deleteDoc(doc(db, "courses", faq.courseId, "faqs", faq.id))
      toast.success("Question deleted")
    } catch (error: any) {
      if (error.code === 'permission-denied') {
        toast.error("Permission Denied. Check your admin permissions.")
      } else {
        toast.error("Failed to delete")
      }
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-serif font-bold text-slate-900">FAQ Moderation</h2>
        <p className="text-xs text-slate-500 uppercase tracking-widest font-bold mt-1">Answer student inquiries across all courses</p>
      </div>

      {loading && faqs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-[#b48c36]">
          <Loader2 className="animate-spin mb-4" size={32} />
          <p className="text-[10px] font-bold uppercase tracking-[0.3em]">Querying Database...</p>
        </div>
      ) : faqs.length === 0 ? (
        <div className="bg-white border border-slate-200 p-12 text-center">
          <HelpCircle className="mx-auto text-slate-200 mb-4" size={48} />
          <p className="text-slate-500 font-serif">Registry is empty. No questions yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {faqs.map((faq) => (
            <div key={faq.id} className="bg-white border border-slate-200 p-6 transition-all hover:shadow-md">
              <div className="flex flex-col md:flex-row justify-between gap-6">
                <div className="flex-1 space-y-4">
                  <div className="flex items-center gap-3">
                    <span className="bg-slate-950 text-[#b48c36] px-2 py-1 text-[9px] font-bold uppercase tracking-wider flex items-center gap-1.5">
                       <BookOpen size={10} /> {faq.courseTitle || "Unknown"}
                    </span>
                  </div>

                  <div>
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Inquiry from {faq.userName}</p>
                    <p className="text-base font-serif font-medium text-slate-950 leading-relaxed">"{faq.question}"</p>
                  </div>

                  {faq.answer ? (
                    <div className="bg-slate-50 p-4 border-l-2 border-[#b48c36] transform translate-x-2">
                       <div className="flex items-center gap-2 mb-1">
                          <UserCheck size={12} className="text-[#b48c36]" />
                          <p className="text-[9px] font-bold text-[#b48c36] uppercase tracking-widest">Official Response</p>
                       </div>
                       <p className="text-sm text-slate-700 leading-relaxed font-sans">{faq.answer}</p>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                       <textarea 
                         rows={2} 
                         value={replyText[faq.id] || ""}
                         onChange={(e) => setReplyText(prev => ({ ...prev, [faq.id]: e.target.value }))}
                         placeholder="Type your official answer here..." 
                         className="flex-1 bg-slate-50 border border-slate-200 p-3 text-sm focus:outline-none focus:border-[#b48c36] placeholder:text-slate-400"
                       />
                       <button 
                         onClick={() => handleAnswer(faq)}
                         disabled={!authReady}
                         className="bg-slate-950 text-white px-5 flex items-center justify-center transition-colors hover:bg-[#b48c36] disabled:opacity-50"
                       >
                          <Send size={16} />
                       </button>
                    </div>
                  )}
                </div>

                <div className="shrink-0 text-right">
                  <button 
                    onClick={() => toggleVisibility(faq)}
                    disabled={!authReady}
                    className={`p-3 transition-colors disabled:opacity-30 ${(faq as any).isVisible === false ? 'text-rose-500' : 'text-slate-400 hover:text-emerald-500'}`}
                    title={(faq as any).isVisible === false ? "Show" : "Hide"}
                  >
                    {(faq as any).isVisible === false ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                  <button 
                    onClick={() => deleteFaq(faq)}
                    disabled={!authReady}
                    className="p-3 text-slate-400 hover:text-red-500 transition-colors disabled:opacity-30"
                    title="Delete Question"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
